// Rdzeń motywu (@theme/core) — kontrakt: docs/ARCHITEKTURA.md §5.2.

export const Theme = window.Theme || {};

export const EVENTS = {
  cartChange: 'theme:cart:change',
  cartAdd: 'theme:cart:add',
  cartError: 'theme:cart:error',
  variantChange: 'theme:variant:change',
  headerChange: 'theme:header:change',
  drawerOpen: 'theme:drawer:open',
  drawerClose: 'theme:drawer:close',
};

export function emit(name, detail, target = document) {
  const event = new CustomEvent(name, { detail, bubbles: true });
  target.dispatchEvent(event);
  return event;
}

export function debounce(fn, ms) {
  let timer;
  const run = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
  run.cancel = () => clearTimeout(timer);
  return run;
}

export function onIdle(cb, timeout = 4000) {
  return window.requestIdleCallback ? requestIdleCallback(cb, { timeout }) : setTimeout(cb, 1);
}

export const mq = (minWidth) => matchMedia(`(min-width: ${minWidth}px)`);
export const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
export const currencyRate = () => Number(window.Shopify?.currency?.rate) || 1;

// Jak filtr money w Liquid („2.199,99 zł”).
export function formatMoney(cents, { withCurrency = true } = {}) {
  const [int, dec] = ((Number(cents) || 0) / 100).toFixed(2).split('.');
  const amount = `${int.replace(/\B(?=(\d{3})+$)/g, '.')},${dec}`;
  return withCurrency ? `${amount} ${Theme.currency?.symbol || 'zł'}` : amount;
}

export function plural(n, one, few, many) {
  const value = Math.abs(Math.trunc(Number(n) || 0));
  if (value === 1) return one;
  const unit = value % 10;
  const tens = value % 100;
  return unit >= 2 && unit <= 4 && (tens < 12 || tens > 14) ? few : many;
}

export function strings(el) {
  for (let node = el; node; node = node.parentElement) {
    const script = node.querySelector(':scope > script[data-strings]');
    if (script) return JSON.parse(script.textContent);
  }
  return {};
}

let announceTimer;
export function announce(text, ms = 5000) {
  const region = document.getElementById('LiveRegion');
  if (!region || !text) return;
  clearTimeout(announceTimer);
  region.textContent = '';
  // Przerwa: czytnik ogłosi też powtórzony tekst.
  announceTimer = setTimeout(() => {
    region.textContent = text;
    announceTimer = setTimeout(() => (region.textContent = ''), ms);
  }, 50);
}

const bursts = new WeakMap();
export function burst(button) {
  if (!button || !Theme.settings?.addToCartBurst) return;
  const slot = button.querySelector('[data-burst]');
  const template = document.getElementById('burst-icon');
  if (slot && !slot.firstElementChild && template) slot.append(template.content.cloneNode(true));
  const token = {};
  bursts.set(button, token);
  button.classList.remove('is-added');
  void button.offsetWidth; // restart przejść
  button.classList.add('is-added');
  requestAnimationFrame(async () => {
    // Bez animacji nieskończonych (ikona karty przy hover) — nigdy się nie kończą.
    const finite = button.getAnimations({ subtree: true }).filter((a) => a.effect?.getComputedTiming().endTime !== Infinity);
    await Promise.all(finite.map((a) => a.finished.catch(() => {})));
    setTimeout(() => bursts.get(button) === token && button.classList.remove('is-added'), 800);
  });
}

const FOCUSABLE = ':is(a[href],button,input:not([type=hidden]),select,textarea,summary,[tabindex]):not([tabindex="-1"],[inert],[inert] *)';

export function trapFocus(el) {
  const controller = new AbortController();
  const { signal } = controller;
  const visible = (node) =>
    !node.disabled && (node.checkVisibility?.({ visibilityProperty: true, checkVisibilityCSS: true }) ?? node.getClientRects().length > 0);
  const items = () => [...el.querySelectorAll(FOCUSABLE)].filter(visible);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const list = items();
    const edge = event.shiftKey ? list[0] : list.at(-1);
    if (list.length && el.contains(document.activeElement) && document.activeElement !== edge) return;
    event.preventDefault();
    (list.length ? (event.shiftKey ? list.at(-1) : list[0]) : el).focus();
  }, { signal });
  document.addEventListener('focusin', (event) => el.contains(event.target) || (items()[0] || el).focus(), { signal });
  return () => controller.abort();
}

const lockOwners = new Set();
export function lockScroll(owner) {
  lockOwners.add(owner);
  syncScrollLock();
}

export function unlockScroll(owner) {
  lockOwners.delete(owner);
  syncScrollLock();
}

// Blokuje html[scroll-lock] (base.css); gutter zostawia miejsce po pasku. Odłączeni właściciele odpadają.
function syncScrollLock() {
  for (const owner of lockOwners) if (owner instanceof Element && !owner.isConnected) lockOwners.delete(owner);
  const root = document.documentElement;
  const locked = lockOwners.size > 0;
  if (locked === root.hasAttribute('scroll-lock')) return;
  root.style.scrollbarGutter = locked && innerWidth > root.clientWidth ? 'stable' : '';
  root.toggleAttribute('scroll-lock', locked);
}

const styles = new Map();
// Spełnia się też po błędzie wczytania — treść i tak się pokazuje.
export function loadStyle(href) {
  if (!href) return Promise.resolve();
  const url = new URL(href, location.href).href;
  if (!styles.has(url)) {
    styles.set(url, new Promise((resolve) => {
      let link = [...document.querySelectorAll('link[rel="stylesheet"]')].find((node) => node.href === url);
      if (link?.sheet) return resolve();
      if (link) setTimeout(resolve, 3000); // mógł już zgłosić błąd
      link ??= document.head.appendChild(Object.assign(document.createElement('link'), { rel: 'stylesheet', href: url }));
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', resolve, { once: true });
    }));
  }
  return styles.get(url);
}

let eventsModule;
const shopifyEvents = () => (eventsModule ??= import('@shopify/events'));

export async function standardEvent(name, payload, target = document) {
  try {
    const EventClass = Object.values(await shopifyEvents()).find((value) => value?.eventName === name);
    EventClass && target.dispatchEvent(new EventClass(payload));
  } catch (error) {
    console.warn(error);
  }
}

const sectionRequests = new Map();
const sectionCache = new Map();

// Nowsze żądanie tej samej sekcji przerywa poprzednie (AbortError).
export async function fetchSection(sectionId, { url = location.href, params, signal, cache = true } = {}) {
  const target = new URL(url, location.href);
  const extra = new URLSearchParams(params);
  for (const key of new Set(extra.keys())) target.searchParams.delete(key);
  for (const [key, value] of extra) target.searchParams.append(key, value); // powtórzone klucze (filtry) zostają
  target.searchParams.set('section_id', sectionId);
  target.searchParams.sort();
  target.hash = '';
  const useCache = cache && !Theme.designMode;
  sectionRequests.get(sectionId)?.abort();
  let html = useCache ? sectionCache.get(target.href) : undefined;
  if (html === undefined) {
    const controller = new AbortController();
    sectionRequests.set(sectionId, controller);
    if (signal?.aborted) controller.abort(signal.reason);
    signal?.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
    try {
      const response = await fetch(target, { signal: controller.signal });
      if (!response.ok) throw new Error(`${sectionId}: HTTP ${response.status}`);
      html = await response.text();
      controller.signal.throwIfAborted();
    } finally {
      if (sectionRequests.get(sectionId) === controller) sectionRequests.delete(sectionId);
    }
    if (useCache) sectionCache.set(target.href, html);
    if (sectionCache.size > 20) sectionCache.delete(sectionCache.keys().next().value);
  }
  return new DOMParser().parseFromString(html, 'text/html');
}

// Nazwa fragmentu jest unikalna w `root`. Zwraca wstawione węzły.
export function swapFragments(fromDoc, root = document) {
  const swapped = [];
  for (const current of root.querySelectorAll('[data-fragment]')) {
    if (!current.isConnected) continue; // zastąpiony z nadrzędnym
    const next = fromDoc.querySelector(`[data-fragment="${CSS.escape(current.dataset.fragment)}"]`);
    if (!next || next.dataset.fragmentKey === current.dataset.fragmentKey) continue;
    const focused = current.contains(document.activeElement) && document.activeElement.closest('[data-focus-key]');
    const node = document.importNode(next, true);
    current.replaceWith(node);
    if (focused) node.querySelector(`[data-focus-key="${CSS.escape(focused.dataset.focusKey)}"]`)?.focus({ preventScroll: true });
    reinitAppScripts(node);
    swapped.push(node);
  }
  return swapped;
}

// Wstawione skrypty się nie wykonują — tworzymy je na nowo.
export function reinitAppScripts(root) {
  for (const old of root.querySelectorAll('.shopify-app-block script[src]')) {
    const script = document.createElement('script');
    for (const { name, value } of old.attributes) script.setAttribute(name, value);
    old.replaceWith(script);
  }
}

// `standard(cart)` → [nazwa, payload] zdarzenia shopify:* wysyłanego po sukcesie.
async function cartRequest(route, action, body, { sections = [], source, sourceEl } = {}, standard) {
  try {
    const ids = new Set(sections);
    for (const el of document.querySelectorAll('[data-cart-section]')) ids.add(el.dataset.cartSection);
    const list = [...ids].filter(Boolean).slice(0, 5).join(','); // limit Shopify
    const response = await fetch(Theme.routes[route], {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(list ? { ...body, sections: list, sections_url: location.pathname } : body),
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw Object.assign(new Error(data.description || data.errors || data.message), { status: data.status || response.status });
    }
    const { sections: html, items, ...rest } = data;
    // Leniwy cart.js ma słuchać zdarzeń.
    const [cartData] = await Promise.all([action === 'add' ? cart.get().catch(() => null) : { ...rest, items }, import('@theme/cart').catch(console.warn)]);
    if (action === 'add') emit(EVENTS.cartAdd, { items, cart: cartData, source, sourceEl });
    emit(EVENTS.cartChange, { cart: cartData, action, source, sections: html });
    const [name, payload] = standard?.(cartData) || [];
    if (name) {
      const detail = { source, sections: html, items };
      shopifyEvents().then(({ CartLinesUpdateEvent: { createCartFromAjaxResponse: toCart } }) => {
        const promise = Promise.resolve({ cart: cartData && toCart(cartData), detail });
        standardEvent(name, { ...payload, promise }, sourceEl?.isConnected ? sourceEl : document);
      }, console.warn);
    }
    return { cart: cartData, items, sections: html };
  } catch (error) {
    // Komunikat Shopify (np. brak towaru) zostaje; błąd sieci zastępuje tekst motywu.
    const message = (error.status && error.message) || Theme.strings?.cartError || error.message;
    emit(EVENTS.cartError, { message, source, sourceEl });
    standardEvent('shopify:cart:error', { error: message, code: error.status === 422 ? 'INVALID' : 'SERVICE_UNAVAILABLE' });
    throw Object.assign(new Error(message), { status: error.status });
  }
}

export const cart = {
  async get() {
    const response = await fetch(Theme.routes.cartJs, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart: HTTP ${response.status}`);
    return response.json();
  },

  add: (items, options = {}) =>
    cartRequest('cartAdd', 'add', { items }, options, () => ['shopify:cart:lines-update', {
      action: 'add',
      context: options.sourceEl?.closest('dialog') ? 'dialog' : 'product',
      lines: items.map(({ id, quantity = 1 }) => ({ merchandiseId: id, quantity })),
    }]),

  // `id` = klucz linii. Przy `line` klucz bierzemy z odpowiedzi, więc usuwa się tylko przez `id`.
  change: (change, options) =>
    cartRequest('cartChange', 'change', change, options, ({ items }) => ['shopify:cart:lines-update', {
      action: change.quantity ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: change.id ?? (change.quantity ? items[change.line - 1]?.key : null), quantity: change.quantity }],
    }]),

  update(body, options) {
    const standard = 'discount' in body
      ? ['shopify:cart:discount-update', { discountCodes: String(body.discount).split(',').filter(Boolean).map((code) => ({ code })) }]
      : 'note' in body && ['shopify:cart:note-update', { context: 'cart', note: body.note }];
    return cartRequest('cartUpdate', 'update', body, options, () => standard);
  },
};

// Dodanie z karty (jeden wariant); bez JS formularz wysyła się zwykłym POST.
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!form.matches?.('form[data-card-add]')) return;
  event.preventDefault();
  if (form.classList.contains('is-loading')) return;
  const button = event.submitter || form.querySelector('[type="submit"]');
  const data = new FormData(form);
  form.classList.add('is-loading');
  button?.setAttribute('aria-busy', 'true');
  burst(button);
  try {
    await cart.add([{ id: data.get('id'), quantity: Number(data.get('quantity')) || 1 }], { source: 'card', sourceEl: button || form });
    announce(Theme.strings?.added);
  } catch (error) {
    announce(error.message);
  } finally {
    form.classList.remove('is-loading');
    button?.removeAttribute('aria-busy');
  }
});

let quickAddPreloaded = false;
function preloadQuickAdd() {
  if (quickAddPreloaded) return;
  quickAddPreloaded = true;
  const { quickAdd, product, mediaGallery, productCss, galleryCss } = Theme.assets || {};
  // Pomija to, co strona już ma (PDP: moduły wykonane, arkusze w <head>).
  const links = [[quickAdd], [product, 'product-info'], [mediaGallery, 'media-gallery']]
    .filter(([href, tag]) => href && !customElements.get(tag))
    .map(([href]) => ({ rel: 'modulepreload', href }));
  for (const href of [productCss, galleryCss]) href && !document.querySelector(`link[href="${href}"]`) && links.push({ rel: 'preload', as: 'style', href });
  for (const link of links) document.head.append(Object.assign(document.createElement('link'), link));
}

function onCardIntent(event) {
  if (!event.target.closest?.('product-card')?.querySelector('[data-quick-add]')) return;
  preloadQuickAdd();
  document.removeEventListener('pointerover', onCardIntent);
  document.removeEventListener('focusin', onCardIntent);
}
document.addEventListener('pointerover', onCardIntent, { passive: true });
document.addEventListener('focusin', onCardIntent);

// Szybkie dodawanie: modal od razu (ładowanie, blokada przewijania), moduł i style równolegle.
let quickAddRequest = 0;
document.addEventListener('click', async (event) => {
  const trigger = event.target.closest?.('[data-quick-add]');
  if (!trigger || event.defaultPrevented) return;
  event.preventDefault();
  const modal = document.getElementById('QuickAddModal');
  const url = trigger.dataset.quickAdd;
  if (!modal) return void (location.href = url);
  const request = ++quickAddRequest;
  modal.classList.add('is-loading');
  modal.setAttribute('aria-busy', 'true');
  if (!modal.open) {
    modal.showModal();
    lockScroll(modal);
    modal.addEventListener('close', () => unlockScroll(modal), { once: true });
  }
  try {
    const { productCss, galleryCss } = Theme.assets || {};
    const [module] = await Promise.all([import('@theme/quick-add'), loadStyle(productCss), loadStyle(galleryCss)]);
    // Zamknięty w trakcie ładowania albo zastąpiony nowszym kliknięciem — nie otwieramy ponownie.
    if (request === quickAddRequest && modal.open) await module.open({ url, trigger });
  } catch (error) {
    if (error?.name === 'AbortError' || !modal.open) return;
    console.error(error);
    modal.close();
    location.href = url;
  } finally {
    if (request === quickAddRequest) {
      modal.classList.remove('is-loading');
      modal.removeAttribute('aria-busy');
    }
  }
});

// <details data-close-outside="[mobile|desktop]">: klik poza zamyka (wartość zawęża do <750 / ≥750).
// composedPath, bo cel mógł już zostać podmieniony przez wcześniejszy handler.
document.addEventListener('click', (event) => {
  const path = event.composedPath();
  for (const details of document.querySelectorAll('details[data-close-outside][open]')) {
    const skip = details.dataset.closeOutside === (mq(750).matches ? 'mobile' : 'desktop');
    if (!skip && !path.includes(details)) details.open = false;
  }
});

// Przewinięcie zapamiętane przy pushState (filtry, stronicowanie), przywracane przy Wstecz/Dalej.
const pushState = history.pushState;
history.pushState = function (...args) {
  history.replaceState({ ...history.state, scrollTop: scrollY }, '');
  return pushState.apply(this, args);
};
addEventListener('popstate', ({ state }) => {
  if (typeof state?.scrollTop !== 'number') return;
  const top = Math.min(state.scrollTop, document.documentElement.scrollHeight - innerHeight);
  requestAnimationFrame(() => scrollTo({ top, behavior: 'instant' }));
});

function afterLoad() {
  onIdle(() => {
    const template = document.querySelector('main[data-template]')?.dataset.template || '';
    standardEvent('shopify:page:view', { page: { template, title: document.title, url: location.href } });
    // Idle tylko z myszą i bez Save-Data; dotyk dostaje moduły na intencję (pointerover/focusin karty).
    if (matchMedia('(hover: hover) and (pointer: fine)').matches && !navigator.connection?.saveData && document.querySelector('[data-quick-add]')) preloadQuickAdd();
    if (Theme.designMode) return;
    // KAR-23: karta (view-event-payload) wysyła shopify:product:view po wejściu w widok, także wstawiona później.
    shopifyEvents().then(({ createViewEventElement: create }) => {
      if (create && !customElements.get('product-card')) customElements.define('product-card', create(HTMLElement, { defaultTrigger: 'intersect' }));
    }, console.warn);
  }, 5000);
}
if (document.readyState === 'complete') afterLoad();
else addEventListener('load', afterLoad, { once: true });
